/**
 * PlanFormModal (Spec 201) — create or edit a Backup Plan's definition.
 *
 * A plan binds a schedule + retention + destination. CREATE also offers a bulk "create & protect"
 * picker (search/filter many lakehouses/warehouses, then on save assign the destination, grant the
 * SP, and attach them as targets) — the fast path to stand up a new plan. EDIT only changes the
 * plan's definition and shows its targets read-only; entity↔plan assignment is managed on the
 * Management tab.
 *
 * Author: Peter Dobler (Dobler Data Solutions) · Feature 201
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Database, Warehouse, Search, Loader2, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { Modal, Input, Textarea, Select, Checkbox, Button, Badge, LoadingState } from '@/components/ui';
import { plansApi, BackupPlanDetail } from '../../api/plans';
import { schedulesApi } from '../../api/schedules';
import { retentionApi } from '../../api/retention';
import { assignmentApi, AssignableEntity } from '../../api/assignment';
import { useAuth } from '../../hooks/useAuth';
import { textMatch } from '../common/ListFilterBar';

const envVariant: Record<string, 'success' | 'info' | 'warning' | 'default'> = {
  prod: 'success',
  dev: 'info',
  uat: 'warning',
  test: 'default',
};
const keyOf = (e: { entityType: string; entityId: string }) => `${e.entityType}:${e.entityId}`;

/** Run async work in small concurrent batches, reporting progress as each settles. */
async function runBatched<T>(items: T[], fn: (item: T) => Promise<unknown>, onTick: () => void, batchSize = 8): Promise<void> {
  for (let i = 0; i < items.length; i += batchSize) {
    await Promise.allSettled(
      items.slice(i, i + batchSize).map(async (it) => {
        try {
          await fn(it);
        } finally {
          onTick();
        }
      })
    );
  }
}

export interface PlanFormModalProps {
  open: boolean;
  onClose: () => void;
  plan?: BackupPlanDetail | null;
}

export const PlanFormModal: React.FC<PlanFormModalProps> = ({ open, onClose, plan }) => {
  const qc = useQueryClient();
  const editing = !!plan;
  // Spec 214 FR-028: row-click opens this modal for EVERY role — render read-only for
  // Viewer/Manager (their clicks 403'd server-side); legal hold + immutability are
  // ADMIN-only server-side, so only Admins see those buttons.
  const { isOperatorOrAbove, isAdmin } = useAuth();
  const canManage = isOperatorOrAbove();
  const canHold = isAdmin();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scheduleId, setScheduleId] = useState('');
  const [retentionId, setRetentionId] = useState('');
  const [storageId, setStorageId] = useState('');
  const [captureDefs, setCaptureDefs] = useState(false);
  const [targets, setTargets] = useState<Set<string>>(new Set());

  const [q, setQ] = useState('');
  const [envFilter, setEnvFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ label: string; done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  type GrantRow = { workspace: string; status: 'granted' | 'requested' | 'failed'; role: string; detail?: string };
  const [result, setResult] = useState<{ planName: string; targetCount: number; grants: GrantRow[]; active: boolean; attachFailures: string[]; captureAutoEnabled: boolean } | null>(null);

  const schedules = useQuery({ queryKey: ['backup', 'schedule-defs'], queryFn: schedulesApi.list, enabled: open });
  const policies = useQuery({ queryKey: ['backup', 'retention-policies'], queryFn: retentionApi.listPolicies, enabled: open });
  const storages = useQuery({ queryKey: ['backup', 'storage-accounts'], queryFn: assignmentApi.storageAccounts, enabled: open });
  const entities = useQuery({ queryKey: ['backup', 'assignable-entities'], queryFn: assignmentApi.assignableEntities, enabled: open });
  // Spec 202 — plan immutability state (edit only).
  const immutability = useQuery({
    queryKey: ['backup', 'immutability', plan?.id],
    queryFn: () => plansApi.getImmutability(plan!.id),
    enabled: open && editing && !!plan,
  });

  useEffect(() => {
    if (!open) return;
    setError(null);
    setProgress(null);
    setBusy(false);
    setResult(null);
    setQ('');
    setEnvFilter('');
    setTypeFilter('');
    if (plan) {
      setName(plan.name);
      setDescription(plan.description ?? '');
      setScheduleId(plan.scheduleDefinitionId ?? '');
      setRetentionId(plan.retentionPolicyId ?? '');
      setStorageId(plan.storageAccountId ?? '');
      setCaptureDefs(Boolean(plan.captureDefinitions));
      setTargets(new Set(plan.targets.map(keyOf)));
    } else {
      setName('');
      setDescription('');
      setScheduleId('');
      setRetentionId('');
      setStorageId('');
      setCaptureDefs(false);
      setTargets(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, plan?.id]);

  const allEntities = entities.data ?? [];
  const filtered = useMemo(
    () =>
      allEntities
        .filter((e) => !typeFilter || e.entityType === typeFilter)
        .filter((e) => !envFilter || e.envHint === envFilter)
        .filter((e) => textMatch(q, e.name, e.workspaceName, e.entityType, e.envHint))
        .sort((a, b) => (a.workspaceName ?? '').localeCompare(b.workspaceName ?? '') || (a.name ?? '').localeCompare(b.name ?? '')),
    [allEntities, typeFilter, envFilter, q]
  );

  const toggle = (e: AssignableEntity) =>
    setTargets((s) => {
      const n = new Set(s);
      n.has(keyOf(e)) ? n.delete(keyOf(e)) : n.add(keyOf(e));
      return n;
    });
  const allFilteredSelected = filtered.length > 0 && filtered.every((e) => targets.has(keyOf(e)));
  const toggleAllFiltered = () =>
    setTargets((s) => {
      const n = new Set(s);
      if (allFilteredSelected) filtered.forEach((e) => n.delete(keyOf(e)));
      else filtered.forEach((e) => n.add(keyOf(e)));
      return n;
    });

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const input = {
        name: name.trim(),
        description: description || null,
        scheduleDefinitionId: scheduleId || null,
        retentionPolicyId: retentionId || null,
        storageAccountId: storageId || null,
        captureDefinitions: captureDefs, // Spec 214 T067 (backend was wired, UI never sent it)
      };
      setProgress({ label: 'Saving plan…', done: 0, total: 1 });
      const saved = editing ? await plansApi.update(plan!.id, input) : await plansApi.create(input);
      const planId = saved.id;

      // Targets are managed on the Management tab — editing a plan only updates its definition
      // (name / schedule / retention / destination), never its entity assignments.
      if (editing) {
        await qc.invalidateQueries({ queryKey: ['backup'] });
        onClose();
        return;
      }

      const selectedEntities = allEntities.filter((e) => targets.has(keyOf(e)));
      const refs = selectedEntities.map((e) => ({ entityType: e.entityType, entityId: e.entityId }));

      // end-to-end: assign the destination to targets that don't already have one. override=false
      // so a lakehouse/warehouse that already has a primary destination is SKIPPED, not given a
      // duplicate second storage map (it keeps the destination it already has).
      if (storageId && refs.length) {
        setProgress({ label: 'Assigning destination…', done: 0, total: 1 });
        await assignmentApi.bulkAssign(refs, storageId, false).catch(() => undefined);
      }

      // grant the SP once per distinct workspace (best-effort — a CA-blocked grant won't fail the plan)
      const byWs = new Map<string, AssignableEntity>();
      for (const e of selectedEntities) if (e.workspaceId && !byWs.has(e.workspaceId)) byWs.set(e.workspaceId, e);
      const wsReps = [...byWs.values()];
      const grants: GrantRow[] = [];
      if (wsReps.length) {
        let g = 0;
        setProgress({ label: 'Granting service principal…', done: 0, total: wsReps.length });
        await runBatched(
          wsReps,
          async (e) => {
            const ws = e.workspaceName ?? e.workspaceId ?? '—';
            try {
              const r = await assignmentApi.grant(e.entityType, e.entityId, e.workspaceId ?? undefined);
              grants.push({ workspace: ws, status: r.status, role: r.role, detail: r.detail });
            } catch (err) {
              grants.push({ workspace: ws, status: 'failed', role: '—', detail: (err as Error).message });
            }
          },
          () => setProgress({ label: 'Granting service principal…', done: ++g, total: wsReps.length })
        );
      }

      // attach all selected entities as targets (a new plan has no existing targets to detach).
      // Spec 214 FR-029/FR-003: per-item failures are COLLECTED and surfaced — the old
      // allSettled swallow reported workspaces "protected" after every attach 400'd.
      const attachFailures: string[] = [];
      let captureAutoEnabled = false;
      if (selectedEntities.length) {
        let a = 0;
        setProgress({ label: 'Attaching targets…', done: 0, total: selectedEntities.length });
        await runBatched(
          selectedEntities,
          async (e) => {
            try {
              const t = await plansApi.attachTarget(planId, e.entityType, e.entityId);
              if ((t as { captureEnabled?: boolean }).captureEnabled) captureAutoEnabled = true;
            } catch (err) {
              attachFailures.push(`${e.name ?? e.entityId}: ${(err as Error).message}`);
              throw err;
            }
          },
          () => setProgress({ label: 'Attaching targets…', done: ++a, total: selectedEntities.length })
        );
      }

      // New plans default to 'draft' on the backend so config can be staged — but
      // "Create & protect" is a complete, intentional action, so activate it now (user can pause later).
      let active = false;
      try {
        await plansApi.transition(planId, 'activate');
        active = true;
      } catch {
        /* leave as draft if activation is rejected */
      }

      await qc.invalidateQueries({ queryKey: ['backup'] });
      // Surface the per-workspace grant results + per-item attach failures instead of closing silently.
      if (grants.length > 0 || attachFailures.length > 0 || captureAutoEnabled) {
        setResult({
          planName: input.name,
          targetCount: selectedEntities.length - attachFailures.length,
          grants,
          active,
          attachFailures,
          captureAutoEnabled,
        });
      } else {
        onClose();
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const lifecycle = async (action: 'pause' | 'activate' | 'archive') => {
    if (!plan) return;
    setBusy(true);
    try {
      await plansApi.transition(plan.id, action);
      await qc.invalidateQueries({ queryKey: ['backup'] });
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const toggleLegalHold = async () => {
    if (!plan) return;
    setBusy(true);
    try {
      await plansApi.setLegalHold(plan.id, !plan.legalHold);
      await qc.invalidateQueries({ queryKey: ['backup'] });
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  // Spec 202 — toggle storage-enforced WORM (Admin-only server-side; lock derived from retention).
  const toggleImmutable = async () => {
    if (!plan) return;
    setBusy(true);
    setError(null);
    try {
      await plansApi.setImmutability(plan.id, { mode: !immutability.data?.mode });
      await Promise.all([qc.invalidateQueries({ queryKey: ['backup'] }), immutability.refetch()]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const opt = (rows: Array<{ id: string; name: string }> | undefined, none: string) => [
    { value: '', label: none },
    ...(rows ?? []).map((r) => ({ value: r.id, label: r.name })),
  ];

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      size="xl"
      title={editing ? `Edit plan — ${plan!.name}` : 'New backup plan'}
      footer={
        result ? (
          <div className="flex justify-end">
            <Button variant="primary" onClick={onClose}>Done</Button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 text-sm text-[var(--color-text-secondary)]">
              {progress && (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-[var(--color-accent)]" />
                  {progress.label} {progress.total > 1 ? `${progress.done}/${progress.total}` : ''}
                </span>
              )}
              {error && <span className="text-[var(--color-status-danger)]">{error}</span>}
            </div>
            <div className="flex shrink-0 gap-2">
              <Button variant="ghost" onClick={onClose} disabled={busy}>{canManage ? 'Cancel' : 'Close'}</Button>
              {canManage && (
                <Button variant="primary" onClick={submit} disabled={!name.trim() || busy}>
                  {editing ? 'Save plan' : `Create & protect ${targets.size > 0 ? `(${targets.size})` : ''}`}
                </Button>
              )}
            </div>
          </div>
        )
      }
    >
      <div className="space-y-4">
        {result ? (
          <div className="space-y-4">
            <div className="flex items-start gap-2 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-bg-subtle)] p-3">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-status-success)]" />
              <p className="text-sm text-[var(--color-text-secondary)]">
                Plan <strong className="text-[var(--color-text-primary)]">{result.planName}</strong> is{' '}
                <strong className="text-[var(--color-text-primary)]">{result.active ? 'active' : 'saved as draft'}</strong> ·{' '}
                <strong className="text-[var(--color-text-primary)]">{result.targetCount}</strong> target{result.targetCount === 1 ? '' : 's'} protected.
                {result.captureAutoEnabled && <> Definition capture was <strong className="text-[var(--color-text-primary)]">auto-enabled</strong> for the workspace target(s).</>}
              </p>
            </div>
            {/* Spec 214 FR-029: per-item attach failures — never a silent "protected" lie. */}
            {result.attachFailures.length > 0 && (
              <div className="rounded-[var(--radius-md)] border border-[var(--color-status-danger)] bg-[var(--color-bg-subtle)] p-3">
                <p className="mb-1 text-sm font-medium text-[var(--color-status-danger)]">
                  {result.attachFailures.length} target{result.attachFailures.length === 1 ? '' : 's'} could NOT be attached:
                </p>
                <ul className="list-inside list-disc text-xs text-[var(--color-text-secondary)]">
                  {result.attachFailures.slice(0, 8).map((f) => <li key={f}>{f}</li>)}
                  {result.attachFailures.length > 8 && <li>… and {result.attachFailures.length - 8} more</li>}
                </ul>
              </div>
            )}
            <div>
              <p className="mb-1.5 text-sm font-medium text-[var(--color-text-primary)]">
                Service-principal grants ({result.grants.length} workspace{result.grants.length === 1 ? '' : 's'})
              </p>
              <div className="max-h-[44vh] divide-y divide-[var(--color-border)] overflow-y-auto rounded-[var(--radius-md)] border border-[var(--color-border)]">
                {result.grants.map((g, i) => (
                  <div key={`${g.workspace}-${i}`} className="flex items-center justify-between gap-3 px-3 py-2">
                    <span className="flex min-w-0 items-center gap-2">
                      {g.status === 'granted' ? (
                        <CheckCircle2 className="h-4 w-4 shrink-0 text-[var(--color-status-success)]" />
                      ) : g.status === 'requested' ? (
                        <Clock className="h-4 w-4 shrink-0 text-[var(--color-accent)]" />
                      ) : (
                        <XCircle className="h-4 w-4 shrink-0 text-[var(--color-status-danger)]" />
                      )}
                      <span className="truncate text-sm text-[var(--color-text-primary)]">{g.workspace}</span>
                    </span>
                    <span className="shrink-0">
                      {g.status === 'granted' ? (
                        <Badge variant="success">{g.role}</Badge>
                      ) : g.status === 'requested' ? (
                        <Badge variant="info">requested</Badge>
                      ) : (
                        <span className="text-xs text-[var(--color-status-danger)]" title={g.detail}>failed</span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
              {result.grants.some((g) => g.status === 'requested') && (
                <p className="mt-2 text-xs text-[var(--color-text-secondary)]">
                  Workspace grants are an <strong>admin step</strong> — run{' '}
                  <code className="rounded bg-[var(--color-bg-subtle)] px-1">scripts/Grant-FabricControlSPWorkspaceAccess.ps1</code>{' '}
                  for these workspaces. The platform SP can&apos;t self-grant; the plan, assignments, and backup-enable are already saved.
                </p>
              )}
              {result.grants.some((g) => g.status === 'failed') && (
                <p className="mt-2 text-xs text-[var(--color-status-warning)]">
                  Some grants reported an error — see each row&apos;s detail (the plan + assignments are already saved).
                </p>
              )}
            </div>
          </div>
        ) : (
        <>
        {/* lifecycle actions (edit only) */}
        {editing && plan && (
          <div className="flex flex-wrap items-center gap-2 rounded-[var(--radius-md)] bg-[var(--color-bg-subtle)] p-2">
            <Badge variant={plan.status === 'active' ? 'success' : plan.status === 'paused' ? 'warning' : 'default'}>{plan.status}</Badge>
            {plan.legalHold && <Badge variant="warning">legal hold</Badge>}
            {immutability.data?.mode &&
              (immutability.data.probeStatus && immutability.data.probeStatus !== 'ok' ? (
                <Badge variant="warning">immutable (degraded)</Badge>
              ) : (
                <Badge variant="info">
                  immutable · {immutability.data.effectiveLockDays ?? immutability.data.derivedLockDays ?? '?'}d
                </Badge>
              ))}
            <span className="grow" />
            {/* Spec 214 FR-028: lifecycle = Operator+; legal hold + immutability = ADMIN only
                (server-enforced) — read roles see status badges only. */}
            {canManage && (
              plan.status === 'active' ? (
                <Button size="sm" variant="outline" onClick={() => lifecycle('pause')} disabled={busy}>Pause</Button>
              ) : (
                <Button size="sm" variant="outline" onClick={() => lifecycle('activate')} disabled={busy}>Activate</Button>
              )
            )}
            {canHold && (
              <>
                <Button size="sm" variant="outline" onClick={toggleLegalHold} disabled={busy}>
                  {plan.legalHold ? 'Remove legal hold' : 'Set legal hold'}
                </Button>
                <Button size="sm" variant="outline" onClick={toggleImmutable} disabled={busy || immutability.isLoading}>
                  {immutability.data?.mode ? 'Disable immutability' : 'Make immutable'}
                </Button>
              </>
            )}
            {canManage && (
              <Button size="sm" variant="ghost" onClick={() => lifecycle('archive')} disabled={busy}>Archive</Button>
            )}
          </div>
        )}

        {/* policy config */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input label="Plan name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Prod ODS — daily" />
          <Select label="Schedule" value={scheduleId} onChange={(e) => setScheduleId(e.target.value)} options={opt(schedules.data, '— none —')} />
          {/* Spec 214 FR-022 (D6): the plan policy is the FALLBACK — a source-level policy wins. */}
          <Select label="Retention policy (fallback — source-level policies win)" value={retentionId} onChange={(e) => setRetentionId(e.target.value)} options={opt(policies.data, '— resolve from source / default —')} />
          <Select
            label="Destination"
            value={storageId}
            onChange={(e) => setStorageId(e.target.value)}
            options={[{ value: '', label: '— none —' }, ...(storages.data ?? []).map((s) => ({ value: s.id, label: s.name }))]}
          />
          <Textarea label="Description" value={description} onChange={(e) => setDescription(e.target.value)} rows={1} />
          {/* Spec 214 T067 / Spec 205 FR-009: workspace item-definition capture (workspace-complete DR).
              Attaching a workspace target auto-enables this (Clarify Q4). */}
          <Checkbox
            label="Capture workspace item definitions (workspace-complete DR)"
            checked={captureDefs}
            onChange={(e) => setCaptureDefs(e.target.checked)}
            disabled={!canManage}
          />
        </div>

        {/* targets — EDIT: read-only list (assignments are managed on the Management tab);
            CREATE: searchable bulk picker for the "create & protect" fast path. */}
        {editing ? (
          <div className="rounded-[var(--radius-md)] border border-[var(--color-border)]">
            <div className="border-b border-[var(--color-border)] px-3 py-2 text-sm font-medium text-[var(--color-text-primary)]">
              Protected entities ({plan!.targets.length})
            </div>
            {plan!.targets.length === 0 ? (
              <p className="p-4 text-sm text-[var(--color-text-secondary)]">
                No entities are assigned to this plan yet — assign them on the Management tab.
              </p>
            ) : (
              <div className="max-h-[34vh] divide-y divide-[var(--color-border)] overflow-y-auto">
                {plan!.targets.map((t) => {
                  const e = allEntities.find((x) => keyOf(x) === keyOf(t));
                  const Icon = t.entityType === 'warehouse' ? Warehouse : Database;
                  return (
                    <div key={keyOf(t)} className="flex items-center gap-3 px-3 py-2">
                      <Icon className="h-4 w-4 shrink-0 text-[var(--color-text-secondary)]" />
                      <span className="min-w-0 grow">
                        <span className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium text-[var(--color-text-primary)]">{e?.name ?? t.entityId}</span>
                          {e?.envHint && <Badge variant={envVariant[e.envHint] ?? 'default'}>{e.envHint}</Badge>}
                        </span>
                        <span className="block truncate text-xs text-[var(--color-text-secondary)]">{e?.workspaceName ?? 'Unknown workspace'} · {t.entityType}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-[var(--radius-md)] border border-[var(--color-border)]">
            <div className="flex flex-wrap items-end gap-2 border-b border-[var(--color-border)] p-2">
              <div className="relative grow">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-secondary)]" />
                <Input placeholder="Search entities by name or workspace…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
              </div>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                options={[{ value: '', label: 'All types' }, { value: 'lakehouse', label: 'Lakehouse' }, { value: 'warehouse', label: 'Warehouse' }, { value: 'workspace', label: 'Workspace' }]}
              />
              <Select
                value={envFilter}
                onChange={(e) => setEnvFilter(e.target.value)}
                options={[{ value: '', label: 'All envs' }, { value: 'prod', label: 'prod' }, { value: 'uat', label: 'uat' }, { value: 'dev', label: 'dev' }, { value: 'test', label: 'test' }]}
              />
              <Button variant="outline" size="sm" onClick={toggleAllFiltered} disabled={filtered.length === 0}>
                {allFilteredSelected ? 'Clear shown' : `Select all ${filtered.length}`}
              </Button>
            </div>
            {entities.isLoading ? (
              <div className="p-4"><LoadingState rows={4} /></div>
            ) : (
              <div className="max-h-[34vh] divide-y divide-[var(--color-border)] overflow-y-auto">
                {filtered.length === 0 ? (
                  <p className="p-4 text-sm text-[var(--color-text-secondary)]">No entities match.</p>
                ) : (
                  filtered.map((e) => {
                    const Icon = e.entityType === 'warehouse' ? Warehouse : Database;
                    return (
                      <label key={keyOf(e)} className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-[var(--color-bg-subtle)]">
                        <Checkbox checked={targets.has(keyOf(e))} onChange={() => toggle(e)} />
                        <Icon className="h-4 w-4 shrink-0 text-[var(--color-text-secondary)]" />
                        <span className="min-w-0 grow">
                          <span className="flex items-center gap-2">
                            <span className="truncate text-sm font-medium text-[var(--color-text-primary)]">{e.name ?? e.entityId}</span>
                            {e.envHint && <Badge variant={envVariant[e.envHint] ?? 'default'}>{e.envHint}</Badge>}
                          </span>
                          <span className="block truncate text-xs text-[var(--color-text-secondary)]">{e.workspaceName ?? 'Unknown workspace'} · {e.entityType}</span>
                        </span>
                      </label>
                    );
                  })
                )}
              </div>
            )}
            <div className="border-t border-[var(--color-border)] px-3 py-1.5 text-right text-xs text-[var(--color-text-secondary)]">
              {targets.size} selected · {filtered.length} shown
            </div>
          </div>
        )}
        <p className="text-xs text-[var(--color-text-secondary)]">
          {editing
            ? 'Entities are assigned to plans on the Management tab — this list is read-only.'
            : "On save, selected entities are protected end-to-end: the destination is assigned, the service principal is granted on each workspace, and they're attached as plan targets."}
        </p>
        </>
        )}
      </div>
    </Modal>
  );
};

export default PlanFormModal;
